import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { ProjectCoverageCategory } from '@kirin/api-client/contracts/projects';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { Badge } from '@/components/ui/badge';
import { StatusCell } from './coverageDisplay';
import {
  formatFramework,
  STATUS_LABEL,
  type StatusKey,
} from './coverageTableView';

/**
 * Cap the rendered rows. The largest catalog is ~40 categories today, so this
 * never truncates in practice — it just stops a future catalog from dumping an
 * unbounded list into every expanded inventory row.
 */
const DRILL_LIST_LIMIT = 50;

export interface CoverageDrillListProps {
  /** Which bucket is being drilled into — drives the heading and footer copy. */
  status: StatusKey;
  /** The categories in this bucket, already in memory from the coverage query. */
  categories: ProjectCoverageCategory[];
  /** Link to the full single-project coverage table, prefiltered to this bucket. */
  detailsHref: string;
  /** Element id so the triggering button's `aria-controls` can point here. */
  id: string;
}

/**
 * KIR-4811: the per-category breakdown behind one of the drawer's coverage
 * counts — which specific security categories are missing / partial / covered /
 * not scanned for this project.
 *
 * Renders from `categories` alone: the drawer's coverage query already holds
 * every audited category, so drilling in costs no extra request.
 */
export function CoverageDrillList({
  status,
  categories,
  detailsHref,
  id,
}: CoverageDrillListProps): ReactElement {
  const label = STATUS_LABEL[status];
  const visible = categories.slice(0, DRILL_LIST_LIMIT);
  const hidden = categories.length - visible.length;

  return (
    <div
      id={id}
      role="region"
      aria-label={`${label} categories`}
      className="mt-1 flex flex-col gap-2 border-t pt-2"
    >
      {visible.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No categories in this bucket.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {visible.map(category => (
            <li
              key={category.id}
              className="flex flex-wrap items-center gap-2 text-xs"
            >
              <StatusCell status={category.status} />
              <span className="min-w-0 truncate font-medium">
                {category.name}
              </span>
              <Badge variant="outline">
                {formatFramework(category.framework, category.reference)}
              </Badge>
              <SeverityBadge riskLevel={category.severity} showIcon />
            </li>
          ))}
          {hidden > 0 && (
            <li className="text-xs text-muted-foreground">+{hidden} more</li>
          )}
        </ul>
      )}
      <Link
        to={detailsHref}
        className="inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline"
      >
        See all {label.toLowerCase()} items
        <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
